import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Grid2,
  IconButton,
} from "@mui/material";

import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import CloseButton from "../../../Components/CloseButton";
import VisualizationSettings from "./VisualizationSettings";
import VisualizationPreview from "./VisualizationPreview";
import CustomIcon from "../../../Components/CustomIcon";
import { PrimaryTooltip } from "../../../Components/StyledComponent";
import { resetBinaryChartData } from "../Common/CommonFunctions";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 8,
    boxShadow: theme.shadows[5],
    width: "1304px",
    maxWidth: "100%",
    backgroundColor: '#F4F4F4',
    margin: 0,
    maxHeight: '100%',
  },
}));

const EditVisualizationDialog = ({ entities, batteryID, selectedDate, onSaveChart, batteryInfo = {}, selectedEntity = null }) => {

  const {t} = useTranslation(['common', 'digital_twin']);
  const [open, setOpen] = useState(false);
  const [previewChartData, setPreviewChartData] = useState({});
  const [chartEntities, setChartEntities] = useState([]);
  const [isChartLoading, setIsChartLoading] = useState(false);

  const handleOpen = () => {
    setOpen(true)
  };

  const handleClose = () => {
    setOpen(false);
  };

  const fetchChartData = useCallback(async (entities) => {
    if (!entities || entities.length === 0) {
      setPreviewChartData({});
      setIsChartLoading(false);
      return;
    }

    setIsChartLoading(true);

    try {
      const resetChartDataResponse = await resetBinaryChartData(entities, selectedDate?.value);

      setPreviewChartData(resetChartDataResponse);
    } catch (error) {
      console.error("Error fetching chart data:", error);
      setPreviewChartData({});
    } finally {
      setIsChartLoading(false);
    }
  }, [selectedDate]);

  const handleOnEntitiesChange = useCallback((newEntities) => {
    setChartEntities(newEntities);
    fetchChartData(newEntities);
  }, [fetchChartData]);

  const handleOnCreate = () => {
    onSaveChart(chartEntities);
    handleClose();
  }

  useEffect(() => {
    if (open && entities) {
      setChartEntities(entities);
      fetchChartData(entities);
    }
  }, [open, entities, fetchChartData])

  return (
    <>
      <PrimaryTooltip title={"Edit Visualization"} placement="bottom">
        <Box>
          <IconButton sx={{
            px: 1.5,
            py: 1,
            borderRadius: '17px',
            '&:hover': {backgroundColor: '#F7F7FF'},
            '&:hover svg': { color: '#6D72F6' }
          }} onClick={handleOpen}>
            <CustomIcon name="edit-icon" height="16px" width="16px" />
          </IconButton>
        </Box>
      </PrimaryTooltip>

      <StyledDialog open={open} onClose={handleClose}>
        <DialogContent sx={{py: 3}}>
          <Grid2 sx={{ mb: 3 }}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3}}>
              <Typography variant="fw500s20c000000" sx={{ lineHeight: '24px' }}>Create Visualisation</Typography>
              <CloseButton onClick={handleClose} />
            </Box>
            <Grid2 container gap={1.5} flexWrap={'nowrap'}>
                <VisualizationSettings batteryID={batteryID} entities={chartEntities} onEntitiesChange={handleOnEntitiesChange} />
                <VisualizationPreview selectedDate={selectedDate} chartData={previewChartData} onSave={handleOnCreate} isEdit={true} isLoading={isChartLoading} batteryInfo={batteryInfo} selectedEntity={selectedEntity} />
            </Grid2>
          </Grid2>
        </DialogContent>
      </StyledDialog>
    </>
  );
};

export default EditVisualizationDialog;
